import React from "react";
import { PixiComponent, useApp } from "@pixi/react";
import { Viewport as PixiViewport } from "pixi-viewport";
import * as PIXI from "pixi.js";

const PixiComponentViewport = PixiComponent( "Viewport",
  {
    create: ( props ) =>
    {
      const { app, ...viewportProps } = props;

      const viewport = new PixiViewport( {
        ticker: props.app.ticker,
        events: props.app.renderer.events,
        screenWidth: props.screenWidth,
        screenHeight: props.screenHeight,
        worldWidth: 10000,
        worldHeight: 10000,
        ...viewportProps
      } );

      // activate plugins
      ( props.plugins || [] ).forEach( ( plugin ) =>
      {
        if( typeof plugin === 'string' )
        {
          viewport[ plugin ]();
        }
        else if( typeof plugin === 'object' && plugin.name )
        {
          viewport[ plugin.name ]( plugin.options || {} );
        }
      } );

      // Ensure the viewport is interactive using new eventMode API
      viewport.eventMode = 'static';

      // Add our custom wheel zoom handler with simple, stable behavior
      const addCustomWheelZoom = () =>
      {
        const canvas = props.app.view;

        // Prevent duplicate event listeners
        if( viewport._customWheelAttached ) return;
        viewport._customWheelAttached = true;

        // Simple configuration
        const config = {
          zoomFactor: 0.015,      // How much to zoom per wheel step (2%)
          minZoom: 0.1,
          maxZoom: 3.0,
          mouseFollowFactor: 1 // How much to follow mouse cursor during zoom in (0 = no follow, 1 = full follow)
        };

        const handleWheel = ( e ) =>
        {
          // Only respond to vertical wheel movements, ignore horizontal
          if( Math.abs( e.deltaY ) < Math.abs( e.deltaX ) || e.deltaY === 0 )
          {
            return;
          }

          e.preventDefault();

          // Get current scale
          const currentScale = viewport.scale.x;

          // Determine zoom direction (normalize deltaY to -1 or 1)
          const zoomDirection = e.deltaY > 0 ? -1 : 1; // Zoom out if deltaY > 0, zoom in if deltaY < 0

          // Calculate new scale
          let newScale;
          if( zoomDirection > 0 )
          {
            // Zoom in: multiply by (1 + factor)
            newScale = currentScale * ( 1 + config.zoomFactor );
          }
          else
          {
            // Zoom out: divide by (1 + factor)
            newScale = currentScale / ( 1 + config.zoomFactor );
          }

          // Clamp the scale
          newScale = Math.max( config.minZoom, Math.min( config.maxZoom, newScale ) );

          // Only proceed if scale actually changes
          if( Math.abs( newScale - currentScale ) > 0.001 )
          {
            // Get mouse position relative to canvas
            const rect = canvas.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;

            let targetX, targetY;

            if( zoomDirection > 0 )
            {
              // Zoom in: follow mouse cursor slightly
              // Get the world position under the mouse cursor
              const worldPosUnderMouse = viewport.toWorld( mouseX, mouseY );

              // Apply the new scale
              viewport.scale.set( newScale );

              // Calculate where the mouse world position is now in screen space
              const newScreenPos = viewport.toScreen( worldPosUnderMouse.x, worldPosUnderMouse.y );

              // Calculate how much to move toward the mouse cursor
              const targetMouseOffsetX = ( mouseX - newScreenPos.x ) * config.mouseFollowFactor;
              const targetMouseOffsetY = ( mouseY - newScreenPos.y ) * config.mouseFollowFactor;

              // Adjust viewport position to follow mouse cursor slightly
              viewport.x += targetMouseOffsetX;
              viewport.y += targetMouseOffsetY;
            }
            else
            {
              // Zoom out: keep center stable (original behavior)
              const centerX = props.screenWidth / 2;
              const centerY = props.screenHeight / 2;
              const worldCenter = viewport.toWorld( centerX, centerY );

              // Apply the new scale
              viewport.scale.set( newScale );

              // Calculate where the center point is now in screen space
              const newScreenCenter = viewport.toScreen( worldCenter.x, worldCenter.y );

              // Calculate the offset needed to keep the world center in the same screen position
              const offsetX = centerX - newScreenCenter.x;
              const offsetY = centerY - newScreenCenter.y;

              // Adjust viewport position to maintain the center
              viewport.x += offsetX;
              viewport.y += offsetY;
            }
          }
        };

        // Add the wheel event listener to the canvas
        canvas.addEventListener( 'wheel', handleWheel, { passive: false } );

        // Store cleanup function
        viewport._customWheelCleanup = () =>
        {
          canvas.removeEventListener( 'wheel', handleWheel );
          viewport._customWheelAttached = false;
        };
      };

      // Add our custom drag handler
      const addCustomDrag = () =>
      {
        const canvas = props.app.view;

        // Prevent duplicate event listeners
        if( viewport._customDragAttached ) return;
        viewport._customDragAttached = true;

        let isDragging = false;
        let lastMousePos = { x: 0, y: 0 };
        let dragStartPos = { x: 0, y: 0 };
        const minDragDistance = 5; // Minimum distance to start drag (prevents accidental drags)

        const handleMouseDown = ( e ) =>
        {
          // Only respond to left mouse button clicks
          if( e.button !== 0 ) return;

          // Ignore if this is a touch event masquerading as a mouse event
          if( e.pointerType === 'touch' ) return;

          const rect = canvas.getBoundingClientRect();
          const mouseX = e.clientX - rect.left;
          const mouseY = e.clientY - rect.top;

          dragStartPos = { x: mouseX, y: mouseY };
          lastMousePos = { x: mouseX, y: mouseY };
          isDragging = false; // Will become true once we move enough

          // Add global mouse move and up listeners
          document.addEventListener( 'mousemove', handleMouseMove );
          document.addEventListener( 'mouseup', handleMouseUp );

          e.preventDefault();
        };

        const handleMouseMove = ( e ) =>
        {
          const rect = canvas.getBoundingClientRect();
          const mouseX = e.clientX - rect.left;
          const mouseY = e.clientY - rect.top;

          // Check if we've moved far enough to start dragging
          if( !isDragging )
          {
            const dragDistance = Math.sqrt(
              Math.pow( mouseX - dragStartPos.x, 2 ) +
              Math.pow( mouseY - dragStartPos.y, 2 )
            );

            if( dragDistance > minDragDistance )
            {
              isDragging = true;

              // Emit drag-start event to match pixi-viewport behavior
              viewport.emit( 'drag-start', {
                screen: { x: mouseX, y: mouseY },
                world: viewport.toWorld( mouseX, mouseY ),
                viewport: viewport
              } );
            } else
            {
              return; // Not far enough to start dragging yet
            }
          }

          // Calculate movement delta
          const deltaX = mouseX - lastMousePos.x;
          const deltaY = mouseY - lastMousePos.y;

          // Apply drag movement to viewport
          viewport.x += deltaX;
          viewport.y += deltaY;

          lastMousePos = { x: mouseX, y: mouseY };

          e.preventDefault();
        };

        const handleMouseUp = ( e ) =>
        {
          // Clean up event listeners
          document.removeEventListener( 'mousemove', handleMouseMove );
          document.removeEventListener( 'mouseup', handleMouseUp );

          // Emit drag-end event if we were dragging
          if( isDragging )
          {
            const rect = canvas.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;

            viewport.emit( 'drag-end', {
              screen: { x: mouseX, y: mouseY },
              world: viewport.toWorld( mouseX, mouseY ),
              viewport: viewport
            } );
          }

          isDragging = false;
        };

        // Add mousedown listener to canvas
        canvas.addEventListener( 'mousedown', handleMouseDown );

        // Store cleanup function
        viewport._customDragCleanup = () =>
        {
          canvas.removeEventListener( 'mousedown', handleMouseDown );
          document.removeEventListener( 'mousemove', handleMouseMove );
          document.removeEventListener( 'mouseup', handleMouseUp );
          viewport._customDragAttached = false;
        };
      };

      // Add the custom wheel zoom and drag
      addCustomWheelZoom();
      addCustomDrag();

      return viewport;
    },
    applyProps( viewport, _oldProps, _newProps )
    {
      const { plugins: oldPlugins, children: oldChildren, ...oldProps } = _oldProps;
      const { plugins: newPlugins, children: newChildren, ...newProps } = _newProps;

      Object.keys( newProps ).forEach( ( p ) =>
      {
        if( oldProps[ p ] !== newProps[ p ] )
        {
          viewport[ p ] = newProps[ p ];
        }
      } );

      // Let viewport handle its own dimensions
    },
    didMount()
    {
      // console.log( "viewport mounted" );
    },
    willUnmount( viewport )
    {
      // Clean up custom wheel handler
      if( viewport._customWheelCleanup )
      {
        viewport._customWheelCleanup();
        viewport._customWheelCleanup = null;
      }

      // Clean up custom drag handler
      if( viewport._customDragCleanup )
      {
        viewport._customDragCleanup();
        viewport._customDragCleanup = null;
      }

      // Safely destroy the viewport
      try
      {
        if( viewport && !viewport.destroyed )
        {
          viewport.destroy( { children: true, texture: false, baseTexture: false } );
        }
      }
      catch( error )
      {
        // Ignore errors during cleanup (common during page refresh)
        console.warn( 'Viewport cleanup error (safe to ignore during page refresh):', error.message );
      }
    }
  } );

// create a component that can be consumed
// that automatically pass down the app
const Viewport = React.forwardRef( ( props, ref ) =>
(
  <PixiComponentViewport ref={ ref } app={ useApp() } { ...props } />
) );

export default Viewport;
